import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import '../../../scss/Graph/Graph.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
    GetGraphByUserId,
    GetTemplates,
    UpdateChartPositions,
    UpdateDashboardName,
} from './../../../reduxToolkit/Graphs/GraphsApi';
import { chartComponents, templateIdToTemplateName, templates } from '../Model/Templates';
import { toast } from 'react-toastify';
import { HttpStatusCode } from '../../../enums/statusCode';

/**
 * @Email : sgarcia@ibl.mx
 * @Author: Susana Garcia Rodriguez
 * @jobTittle: Frontend
 */
const GraphPanelFunctionality = () => {
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [modalShow, setModalShow] = useState(false);
    const [modalShowAddDashboard, setModalShowAddDashboard] = useState(false);
    const [selectedSection, setSelectedSection] = useState(null);
    const [dashboardIdSection, setDashboardIdSection] = useState();
    const [dashboardToDelete, setDashboardToDelete] = useState(null);
    const [filteredDashboards, setFilteredDashboards] = useState([]);
    const [selectedChartId, setSelectedChartId] = useState();
    const [sectionPosition, setSectionPosition] = useState();
    const [showDeleteChartModal, setShowDeleteChartModal] = useState(false);
    const [showDeleteDashboardModal, setShowDeleteDashboardModal] = useState(false);
    const [deletedFlag, setDeletedFlag] = useState(false);
    const [charts, setCharts] = useState({});
    const [tabIndex, setTabIndex] = useState(0);
    const [tabLabels, setTabLabels] = useState({});
    const [editingTab, setEditingTab] = useState(null);
    const [changed, setChanged] = useState(false);
    const [newTabName, setNewTabName] = useState('');
    const containerRef = useRef(null);
    // Selector to retrieve graph data for the user
    const graphData = useSelector((state) => state.GraphsSlice.getGraphByUserId);
    // Selector to retrieve the user ID
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);
    // Boolean to check if there are any dashboards for the user
    const hasDashboards = graphData && graphData.length > 0;
    // State to store the templates for each tab, indexed by tab number
    const [templatesByTab, setTemplatesByTab] = useState({
        0: 'template1',
        1: 'template2',
        2: 'template3',
        3: 'template4',
    });
    // State to store the sections for each tab, using template names for sections
    const [sectionsByTab, setSectionsByTab] = useState({
        0: templates.template1,
        1: templates.template2,
        2: templates.template3,
        3: templates.template4,
    });
    // Function to handle changes in tab label during editing
    const handleTabLabelChange = (index) => {
        setEditingTab(index);
        setNewTabName(tabLabels[index]);
    };
    // Function to save the changes to a tab label (dashboard name) when edited
    const handleTabLabelSave = useCallback(
        (index) => {
            // Tabs are indexed by position, so the dashboard is taken from the same list that built them
            const dashboard = filteredDashboards?.[index];
            if (!dashboard) return;

            const body = {
                id: dashboard.id,
                name: newTabName,
                number: dashboard.number,
                templateId: dashboard.templateId,
                userId,
            };

            dispatch(UpdateDashboardName({ body })).then(({ payload }) => {
              if (payload.data.code === HttpStatusCode.OK) {
                    toast.success(t('has_been_updated_successfully'));
                    dispatch(GetGraphByUserId(userId));
                }
            });

            setEditingTab(null);
            setNewTabName('');
        },
        [dispatch, filteredDashboards, newTabName, userId],
    );
    // Function to handle a click on a section in a dashboard
    const handleSectionClick = (section, dashboardId) => {
        const sectionIndex = sectionsByTab[tabIndex].indexOf(section);
        if (dashboardId && !charts[`${tabIndex}-${section}`]) {
            setSelectedSection(section);
            setModalShow(true);
            setDashboardIdSection(dashboardId);
            setSectionPosition(sectionIndex);
        }
    };
    // Function to open the modal for creating a new dashboard
    const handleCreateClick = () => {
        setModalShowAddDashboard(true);
    };
    // Function to handle the removal of a chart by its ID
    const handleRemoveChart = (chartId) => {
        setShowDeleteChartModal(true);
        setSelectedChartId(chartId);
    };
    // Function to show the modal for removing a dashboard
    /**
     * Opens the delete confirmation for a dashboard.
     * Defaults to the tab being shown, so the modal only has to confirm.
     *
     * @param {number} [index] - Tab number of the dashboard to delete.
     */
    const handleRemoveDashboard = (index) => {
        const position = typeof index === 'number' ? index : tabIndex;

        setDashboardToDelete(filteredDashboards?.[position]?.id);
        setShowDeleteDashboardModal(true);
    };
    // Function to handle the start of a drag event for a chart
    const handleDragStart = (e, chartId, index) => {
        e.dataTransfer.setData('chartId', chartId);
        e.dataTransfer.setData('index', index);
    };
    // Function to handle the drop event for rearranging charts
    const handleDrop = (e, section, index) => {
        const chartId = e.dataTransfer.getData('chartId');
        const oldIndex = e.dataTransfer.getData('index');

        if (chartId && oldIndex !== index) {
            const updatedCharts = { ...charts };

            const temp = updatedCharts[tabIndex][oldIndex];
            updatedCharts[tabIndex][oldIndex] = updatedCharts[tabIndex][index];
            updatedCharts[tabIndex][index] = temp;

            setCharts(updatedCharts);

            const body = [];
            for (const tab in updatedCharts) {
                for (const position in updatedCharts[tab]) {
                    const chart = updatedCharts[tab][position];

                    if (chart && chart.data) {
                        const chartInfo = {
                            chartType: chart.data.chartType,
                            id: chart.data.id,
                            position: position,
                        };

                        body.push({ chartType: chart.data.chartType, id: chart.data.id, position: position });
                    }
                }
            }

            if (body.length > 0) {
                dispatch(UpdateChartPositions({ body }));
            }
        }
    };
    // Function to prevent the default behavior of the drag over event
    const handleDragOver = (e) => {
        e.preventDefault();
    };
    // Effect to dispatch the initial data loading actions (graph data and templates)
    useEffect(() => {
        dispatch(GetGraphByUserId(userId));
        dispatch(GetTemplates());
    }, [dispatch]);
    // Effect to update the state when graph data changes
    useEffect(() => {
        if (graphData && graphData.length > 0) {
            const updatedTabLabels = {};
            const updatedTemplatesByTab = {};
            const updatedCharts = {};
            const updatedSectionsByTab = {};

            const dashboards = graphData.filter((dashboard) =>
                Object.keys(templateIdToTemplateName).includes(dashboard.templateId),
            );

            if (dashboards.length === 0) {
                dispatch(GetGraphByUserId(userId));
            }

            dashboards.forEach((dashboard, index) => {
                const templateName = templateIdToTemplateName[dashboard.templateId];
                updatedTabLabels[index] = dashboard.name;
                updatedTemplatesByTab[index] = templateName;
                updatedSectionsByTab[index] = templates[templateName];

                dashboard.charts.forEach((chart) => {
                    if (!updatedCharts[index]) {
                        updatedCharts[index] = {};
                    }

                    updatedCharts[index][chart.position] = {
                        component: chartComponents[chart.chartType],
                        data: chart,
                    };
                });
            });

            setTabLabels(updatedTabLabels);
            setTemplatesByTab(updatedTemplatesByTab);
            setSectionsByTab(updatedSectionsByTab);
            setCharts(updatedCharts);
            setFilteredDashboards(dashboards);
        }
    }, [graphData]);
    return {
        DEV,
        permission,
        handleCreateClick,
        handleRemoveDashboard,
        hasDashboards,
        tabIndex,
        setTabIndex,
        tabLabels,
        editingTab,
        newTabName,
        setNewTabName,
        handleTabLabelSave,
        t,
        handleTabLabelChange,
        graphData,
        containerRef,
        sectionsByTab,
        charts,
        handleDragOver,
        handleDrop,
        handleDragStart,
        handleRemoveChart,
        handleSectionClick,
        modalShow,
        setModalShow,
        changed,
        setChanged,
        userId,
        modalShowAddDashboard,
        setModalShowAddDashboard,
        dashboardIdSection,
        showDeleteChartModal,
        setShowDeleteChartModal,
        selectedChartId,
        deletedFlag,
        setDeletedFlag,
        dashboardToDelete,
        sectionPosition,
        showDeleteDashboardModal,
        setShowDeleteDashboardModal,
    };
};
export default GraphPanelFunctionality;
